/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { accessAPI, Titulo, Boton, tituloBase } from "../../shared";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputSwitch } from "primereact/inputswitch";
import { Skeleton } from "primereact/skeleton";
import { Divider } from "primereact/divider";

export default function PropuestaSabanaNueva() {
  const { clienteId } = useParams();
  const navigate = useNavigate();

  const [cliente, setCliente] = useState(null);
  const [certificadoTipos, setCertificadoTipos] = useState([]);
  const [monedas, setMonedas] = useState([]);
  const [items, setItems] = useState([]);
  const [adicionales, setAdicionales] = useState([]);
  const [nuevoAdicionalDescripcion, setNuevoAdicionalDescripcion] =
    useState("");
  const [nuevoAdicionalPrecio, setNuevoAdicionalPrecio] = useState(null);
  const [nuevoAdicionalMonedaId, setNuevoAdicionalMonedaId] = useState(null);
  const [nombre, setNombre] = useState("");
  const [masiva, setMasiva] = useState(true);
  const [notas, setNotas] = useState("");
  const [notasInternas, setNotasInternas] = useState("");
  const [loader, setLoader] = useState(true);
  const [emitiendo, setEmitiendo] = useState(false);

  const toast = useRef(null);

  useEffect(() => {
    let pending = 3;
    const done = () => {
      pending -= 1;
      if (pending === 0) setLoader(false);
    };
    accessAPI(
      "GET",
      `admin/cliente/${clienteId}`,
      null,
      (respuesta) => {
        setCliente(respuesta);
        document.title = `Nueva propuesta sábana - ${respuesta.razonsocial}`;
        done();
      },
      () => done()
    );
    accessAPI(
      "GET",
      "admin/propuesta/sabana/referencia",
      null,
      (respuesta) => {
        setCertificadoTipos(respuesta || []);
        done();
      },
      () => done()
    );
    accessAPI(
      "GET",
      "admin/config/moneda?dropdown=true",
      null,
      (respuesta) => {
        setMonedas(respuesta || []);
        if (respuesta?.length > 0) {
          setNuevoAdicionalMonedaId(respuesta[0].value);
        }
        done();
      },
      () => done()
    );
    return () => (document.title = tituloBase());
  }, [clienteId]);

  const certificadoTiposAgregadosIds = [
    ...new Set(items.map((i) => i.certificadoTipoId)),
  ];

  const certificadoTipoOptions = certificadoTipos
    .filter((ct) => !certificadoTiposAgregadosIds.includes(ct.id))
    .map((ct) => ({ label: ct.nombre, value: ct.id }));

  function agregarCertificadoTipo(certificadoTipoId) {
    const ct = certificadoTipos.find((c) => c.id === certificadoTipoId);
    if (!ct) return;
    const nuevos = (ct.actividadtipo_x_certificadotipo || []).map((x) => ({
      certificadoTipoId: ct.id,
      actividadTipoId: x.actividadtipoid,
      precioMonto: x.precio || 0,
      precioMonedaId: x.moneda?.id || monedas[0]?.value || null,
      certificadoTipoNombre: ct.nombre,
      actividadTipoNombre: x.actividadtipo?.nombre || "",
    }));
    setItems((prev) => [...prev, ...nuevos]);
  }

  function actividadTipoOptionsParaCertificado(certificadoTipoId) {
    const ct = certificadoTipos.find((c) => c.id === certificadoTipoId);
    if (!ct) return [];
    const yaPresentes = items
      .filter((i) => i.certificadoTipoId === certificadoTipoId)
      .map((i) => i.actividadTipoId);
    return (ct.actividadtipo_x_certificadotipo || [])
      .filter((x) => !yaPresentes.includes(x.actividadtipoid))
      .map((x) => ({
        label: x.actividadtipo?.nombre || `#${x.actividadtipoid}`,
        value: x.actividadtipoid,
      }));
  }

  function agregarActividad(certificadoTipoId, actividadTipoId) {
    const ct = certificadoTipos.find((c) => c.id === certificadoTipoId);
    if (!ct) return;
    const x = (ct.actividadtipo_x_certificadotipo || []).find(
      (a) => a.actividadtipoid === actividadTipoId
    );
    if (!x) return;
    setItems((prev) => [
      ...prev,
      {
        certificadoTipoId: ct.id,
        actividadTipoId: x.actividadtipoid,
        precioMonto: x.precio || 0,
        precioMonedaId: x.moneda?.id || monedas[0]?.value || null,
        certificadoTipoNombre: ct.nombre,
        actividadTipoNombre: x.actividadtipo?.nombre || "",
      },
    ]);
  }

  function eliminarItem(certificadoTipoId, actividadTipoId) {
    setItems((prev) =>
      prev.filter(
        (i) =>
          !(
            i.certificadoTipoId === certificadoTipoId &&
            i.actividadTipoId === actividadTipoId
          )
      )
    );
  }

  function eliminarCertificadoTipo(certificadoTipoId) {
    setItems((prev) =>
      prev.filter((i) => i.certificadoTipoId !== certificadoTipoId)
    );
  }

  function actualizarPrecio(certificadoTipoId, actividadTipoId, precio) {
    setItems((prev) =>
      prev.map((i) =>
        i.certificadoTipoId === certificadoTipoId &&
        i.actividadTipoId === actividadTipoId
          ? { ...i, precioMonto: precio }
          : i
      )
    );
  }

  function actualizarMoneda(certificadoTipoId, actividadTipoId, monedaId) {
    setItems((prev) =>
      prev.map((i) =>
        i.certificadoTipoId === certificadoTipoId &&
        i.actividadTipoId === actividadTipoId
          ? { ...i, precioMonedaId: monedaId }
          : i
      )
    );
  }

  function referenciaParaItem(item) {
    const ct = certificadoTipos.find((c) => c.id === item.certificadoTipoId);
    if (!ct) return null;
    const x = (ct.actividadtipo_x_certificadotipo || []).find(
      (a) => a.actividadtipoid === item.actividadTipoId
    );
    if (!x || x.precio == null) return null;
    return `Ref: ${x.moneda?.simbolo || ""} ${x.precio}`;
  }

  function agregarAdicional() {
    if (
      !nuevoAdicionalDescripcion.trim() ||
      !nuevoAdicionalPrecio ||
      nuevoAdicionalPrecio <= 0 ||
      !nuevoAdicionalMonedaId
    ) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Complete todos los campos del adicional",
        sticky: true,
      });
      return;
    }
    const monedaSel = monedas.find((m) => m.value === nuevoAdicionalMonedaId);
    setAdicionales((prev) => [
      ...prev,
      {
        descripcion: nuevoAdicionalDescripcion.trim(),
        precio: nuevoAdicionalPrecio,
        monedaid: nuevoAdicionalMonedaId,
        moneda: monedaSel
          ? {
              id: nuevoAdicionalMonedaId,
              nombre: monedaSel.etiqueta,
              simbolo: monedaSel.simbolo || "",
            }
          : null,
      },
    ]);
    setNuevoAdicionalDescripcion("");
    setNuevoAdicionalPrecio(null);
  }

  function eliminarAdicional(index) {
    setAdicionales((prev) => prev.filter((_, i) => i !== index));
  }

  function emitir() {
    if (!nombre.trim()) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Ingrese un nombre para la propuesta",
        sticky: true,
      });
      return;
    }
    if (items.length === 0) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Agregue al menos un item antes de emitir",
        sticky: true,
      });
      return;
    }
    const sinPrecio = items.some(
      (i) => !i.precioMonto || i.precioMonto <= 0 || !i.precioMonedaId
    );
    if (sinPrecio) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Todos los items deben tener precio y moneda",
        sticky: true,
      });
      return;
    }

    setEmitiendo(true);
    accessAPI(
      "POST",
      "admin/propuesta/sabana",
      {
        clienteId: Number(clienteId),
        nombre: nombre.trim(),
        masiva,
        notas,
        notasInternas,
        items: items.map((i) => ({
          certificadoTipoId: i.certificadoTipoId,
          actividadTipoId: i.actividadTipoId,
          precioMonto: i.precioMonto,
          precioMonedaId: i.precioMonedaId,
        })),
        adicionales,
      },
      (respuesta) => {
        setEmitiendo(false);
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "Propuesta sábana emitida",
        });
        navigate(`/propuestasabana/${respuesta.id}`);
      },
      (respuesta) => {
        setEmitiendo(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta?.[0]?.msg || "Error al emitir propuesta",
          sticky: true,
        });
      }
    );
  }

  // Agrupa items por certificadoTipoId preservando orden de aparición
  const grupos = [];
  const vistos = new Set();
  for (const item of items) {
    if (!vistos.has(item.certificadoTipoId)) {
      vistos.add(item.certificadoTipoId);
      grupos.push({
        certificadoTipoId: item.certificadoTipoId,
        certificadoTipoNombre: item.certificadoTipoNombre,
        items: items.filter(
          (i) => i.certificadoTipoId === item.certificadoTipoId
        ),
      });
    }
  }

  if (loader) {
    return (
      <div>
        <Skeleton width="300px" height="2rem" css={{ marginBottom: "1rem" }} />
        <Card>
          <Skeleton width="100%" height="3rem" css={{ marginBottom: "0.5rem" }} />
          <Skeleton width="100%" height="3rem" css={{ marginBottom: "0.5rem" }} />
          <Skeleton width="100%" height="3rem" />
        </Card>
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Titulo
        urlVolver={`/clientes/${clienteId}/facturas`}
        botones={
          <Boton
            callback={emitir}
            loader={emitiendo}
            disabled={items.length === 0 || !nombre.trim()}
          >
            Emitir propuesta
          </Boton>
        }
      >
        Nueva propuesta sábana{cliente ? ` - ${cliente.razonsocial}` : ""}
      </Titulo>

      <Card>
        <Titulo>Datos generales</Titulo>
        <div>
          <label
            css={{ fontWeight: "bold", display: "block", marginBottom: "0.5em" }}
          >
            Nombre de la propuesta
          </label>
          <InputText
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            css={{ width: "100%", maxWidth: "500px" }}
          />
        </div>
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            marginTop: "1rem"
          }}
        >
          <InputSwitch
            checked={masiva}
            onChange={(e) => setMasiva(e.value)}
          />
          <span css={{ fontWeight: "bold" }}>Propuesta más IVA</span>
        </div>
      </Card>

      <Card css={{ marginTop: "1rem" }}>
        <Titulo>Precios por tipo de certificado</Titulo>

        {grupos.length === 0 && (
          <p css={{ color: "var(--grisTextos)" }}>
            Agregue al menos un tipo de certificado para comenzar.
          </p>
        )}

        {grupos.map((grupo) => (
          <div key={grupo.certificadoTipoId} css={{ marginBottom: "1.5rem" }}>
            <div
              css={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0.5rem 0",
                borderBottom: "2px solid var(--surface-300)",
              }}
            >
              <h4 css={{ margin: 0 }}>{grupo.certificadoTipoNombre}</h4>
              <Boton
                texto
                rojo
                chico
                callback={() => eliminarCertificadoTipo(grupo.certificadoTipoId)}
              >
                <i className="pi pi-times" /> Quitar certificado
              </Boton>
            </div>

            {grupo.items.map((item) => {
              const ref = referenciaParaItem(item);
              return (
                <div
                  key={`${item.certificadoTipoId}-${item.actividadTipoId}`}
                  css={{
                    padding: "0.75rem 0",
                    borderBottom: "1px solid var(--surface-200)",
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    flexWrap: "wrap",
                  }}
                >
                  <div css={{ flex: 1, minWidth: "200px" }}>
                    <div>{item.actividadTipoNombre}</div>
                    {ref && (
                      <div
                        css={{
                          color: "var(--grisTextos)",
                          fontSize: "0.85rem",
                        }}
                      >
                        {ref}
                      </div>
                    )}
                  </div>

                  <InputNumber
                    value={item.precioMonto}
                    onValueChange={(e) =>
                      actualizarPrecio(
                        item.certificadoTipoId,
                        item.actividadTipoId,
                        e.value || 0
                      )
                    }
                    mode="decimal"
                    minFractionDigits={0}
                    inputStyle={{ width: "100px" }}
                  />
                  <Dropdown
                    value={item.precioMonedaId}
                    onChange={(e) =>
                      actualizarMoneda(
                        item.certificadoTipoId,
                        item.actividadTipoId,
                        e.value
                      )
                    }
                    options={monedas}
                    optionLabel="etiqueta"
                    optionValue="value"
                    style={{ width: "180px" }}
                  />
                  <Boton
                    texto
                    rojo
                    chico
                    callback={() =>
                      eliminarItem(item.certificadoTipoId, item.actividadTipoId)
                    }
                  >
                    <i className="pi pi-times" />
                  </Boton>
                </div>
              );
            })}

            {actividadTipoOptionsParaCertificado(grupo.certificadoTipoId)
              .length > 0 && (
              <div css={{ marginTop: "0.5rem" }}>
                <Dropdown
                  value={null}
                  onChange={(e) =>
                    agregarActividad(grupo.certificadoTipoId, e.value)
                  }
                  options={actividadTipoOptionsParaCertificado(
                    grupo.certificadoTipoId
                  )}
                  placeholder="Agregar actividad"
                  style={{ width: "100%", maxWidth: "300px" }}
                />
              </div>
            )}
          </div>
        ))}

        <Divider />
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            flexWrap: "wrap",
          }}
        >
          <span css={{ fontWeight: "bold" }}>Agregar tipo de certificado:</span>
          <Dropdown
            value={null}
            onChange={(e) => agregarCertificadoTipo(e.value)}
            options={certificadoTipoOptions}
            placeholder="Seleccionar"
            filter
            style={{ minWidth: "280px" }}
            disabled={certificadoTipoOptions.length === 0}
          />
        </div>
      </Card>

      <Card css={{ marginTop: "1rem" }}>
        <Titulo>Adicionales</Titulo>
        {adicionales.length > 0 && (
          <div css={{ marginBottom: "1rem" }}>
            {adicionales.map((a, index) => (
              <div
                key={index}
                css={{
                  padding: "0.75rem 0",
                  borderBottom: "1px solid var(--surface-200)",
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  flexWrap: "wrap",
                }}
              >
                <div css={{ flex: 1, minWidth: "200px" }}>{a.descripcion}</div>
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <span css={{ fontWeight: "bold" }}>{a.precio}</span>
                  <span css={{ color: "var(--grisTextos)" }}>
                    {a.moneda?.simbolo || a.moneda?.nombre || ""}
                  </span>
                </div>
                <Boton
                  texto
                  rojo
                  chico
                  callback={() => eliminarAdicional(index)}
                >
                  <i className="pi pi-times" />
                </Boton>
              </div>
            ))}
          </div>
        )}
        <div
          css={{
            display: "flex",
            alignItems: "flex-end",
            gap: "0.75rem",
            flexWrap: "wrap",
          }}
        >
          <div css={{ flex: 1, minWidth: "200px" }}>
            <label
              css={{
                display: "block",
                fontSize: "0.85rem",
                color: "var(--grisTextos)",
                marginBottom: "0.25rem",
              }}
            >
              Nombre
            </label>
            <InputText
              value={nuevoAdicionalDescripcion}
              onChange={(e) => setNuevoAdicionalDescripcion(e.target.value)}
              css={{ width: "100%" }}
            />
          </div>
          <div>
            <label
              css={{
                display: "block",
                fontSize: "0.85rem",
                color: "var(--grisTextos)",
                marginBottom: "0.25rem",
              }}
            >
              Monto
            </label>
            <InputNumber
              value={nuevoAdicionalPrecio}
              onValueChange={(e) => setNuevoAdicionalPrecio(e.value)}
              mode="decimal"
              minFractionDigits={0}
              inputStyle={{ width: "100px" }}
            />
          </div>
          <div>
            <label
              css={{
                display: "block",
                fontSize: "0.85rem",
                color: "var(--grisTextos)",
                marginBottom: "0.25rem",
              }}
            >
              Moneda
            </label>
            <Dropdown
              value={nuevoAdicionalMonedaId}
              onChange={(e) => setNuevoAdicionalMonedaId(e.value)}
              options={monedas}
              optionLabel="etiqueta"
              optionValue="value"
              style={{ width: "180px" }}
            />
          </div>
          <Boton texto callback={agregarAdicional}>
            Agregar
          </Boton>
        </div>
      </Card>

      <Card css={{ marginTop: "1rem" }}>
        <Titulo>Notas</Titulo>
        <div css={{ marginBottom: "1rem" }}>
          <label
            css={{ fontWeight: "bold", display: "block", marginBottom: "0.5em" }}
          >
            Notas para el cliente
          </label>
          <InputTextarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            rows={3}
            autoResize
            css={{ width: "100%" }}
          />
        </div>
        <div>
          <label
            css={{ fontWeight: "bold", display: "block", marginBottom: "0.5em" }}
          >
            Notas internas (no se muestran al cliente)
          </label>
          <InputTextarea
            value={notasInternas}
            onChange={(e) => setNotasInternas(e.target.value)}
            rows={3}
            autoResize
            css={{ width: "100%" }}
          />
        </div>
      </Card>
    </>
  );
}
